import type { ModifierCode } from "@/types/medical-codes";

import { type CodeDescriptionMap, normalizeCodes } from "./code-descriptions";

/** Modifier code (upper-cased) -> its reference description. */
export type ModifierDescriptionMap = CodeDescriptionMap;

/** See `normalizeCodes` — same contract, named for this call site. */
export const normalizeModifierCodes = normalizeCodes;

/** Index a lookup response by upper-cased code, dropping label-less rows. */
export const buildModifierDescriptionMap = (
  modifiers: ModifierCode[],
): ModifierDescriptionMap =>
  Object.fromEntries(
    modifiers
      .filter((m) => m.code?.trim() && m.description?.trim())
      .map((m) => [m.code.trim().toUpperCase(), m.description.trim()]),
  );

/**
 * Every modifier on a line, in the line's own order (the order the coder
 * entered them, and the order the claim carries), each paired with its
 * description when the reference set has one.
 *
 * A modifier with NO known description still comes back — with `description`
 * undefined — because this list is the ONLY place the printed Super Bill shows
 * modifiers. Dropping the un-labelled ones would silently erase a modifier from
 * a claim document, which is worse than printing it bare.
 */
export const modifierEntries = (
  codes: string[],
  descriptions: ModifierDescriptionMap | undefined,
): Array<{ code: string; description?: string }> => {
  const seen = new Set<string>();
  return codes.flatMap((raw) => {
    const code = (raw ?? "").trim();
    if (!code) return [];
    const key = code.toUpperCase();
    // A modifier repeated on one line is listed once.
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ code, description: descriptions?.[key] }];
  });
};
